import React, { useEffect } from 'react'
import {
  Button,
  CircularProgress,
  FormControl,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography
} from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import ResourceNotFound from 'components/ResourceNotFound'
import Wrapper from 'components/Wrapper'
import getTableOptions from 'utils/getTableOptions'
import attendeeSlice, { fetchAttendees } from 'store/attendee'
import BasicTable from 'components/BasicTable'
import { Box } from '@mui/material'
import { caseInsensitiveSort } from 'utils'
import { Roles } from 'constants/index'
import { fetchConference } from 'store/conference'
import { Download, EditOutlined as EditIcon } from '@material-ui/icons'
import { downloadAttendeesInCSV } from 'utils/downloadCSV'
import { toast } from 'utils/notify'
import { renderCol } from 'utils/renderColumns'
import { useHistory } from 'react-router-dom'

const AttendeesListing = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const { user } = useSelector((state) => state.auth)
  const { attendees, confFilter, currentPage, pageSize, inProgress } = useSelector(
    (state) => state.attendee
  )
  const { conferences, inProgress: confProgress } = useSelector((state) => state.conference)
  const activeConferences = conferences?.filter((row) => row.isActive === true && !row.isArchived)
  const orderedConferences = caseInsensitiveSort(activeConferences, 'confName')

  const selectedConfernce = orderedConferences.find((conference) => conference?.id === confFilter)

  const isSuperAdmin = user?.role === Roles.SUPER_ADMIN
  const isOrganizer = user?.role === Roles.ORGANIZER
  const usesConferenceFilter = isSuperAdmin || isOrganizer

  // Ensure Organizer's own conferences are loaded when navigating directly to attendees
  useEffect(() => {
    if (isOrganizer) {
      dispatch(fetchConference(user.id))
    }
  }, [dispatch, isOrganizer, user?.id])

  useEffect(() => {
    if (confFilter && usesConferenceFilter && !confProgress) {
      conferences.every((conf) => {
        if (conf.id === confFilter && (conf.isActive === false || conf.isArchived === true)) {
          dispatch(attendeeSlice.actions.setConferenceFilter(orderedConferences[0]?.id))
          return false
        }
        return true
      })
    }
  }, [confProgress, confFilter])

  useEffect(() => {
    if (usesConferenceFilter && !confFilter && !confProgress) {
      dispatch(attendeeSlice.actions.setConferenceFilter(orderedConferences[0]?.id))
    }
  }, [confProgress, confFilter])

  useEffect(() => {
    if (confFilter && usesConferenceFilter) {
      dispatch(fetchAttendees(confFilter))
    } else if (user.role === Roles.ADMIN || user.role === Roles.ACCOUNT_OWNER) {
      dispatch(fetchAttendees(user?.conferenceId))
    }
  }, [dispatch, confFilter, user])

  const handleDownloadCSV = () => {
    if (attendees.length) {
      downloadAttendeesInCSV(attendees, selectedConfernce?.confName)
    } else {
      toast.warning('No attendees found.')
    }
  }

  const tableHead = [
    'Last Name',
    'First Name',
    'Position',
    'Email',
    'Phone Number',
    'Institution',
    'Role',
    {
      name: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => renderCol(meta.label)
      }
    }
  ]

  const formatTableData = (attendees) =>
    attendees.map((attendee) => [
      attendee?.lastName,
      attendee?.firstName,
      attendee?.position,
      attendee?.email,
      attendee?.phoneNumber,
      attendee?.company,
      <Box component="span" className={classes.flexBox}>
        <Typography variant="body2">{attendee?.role ?? 'Attendee'}</Typography>
      </Box>,
      <Tooltip title="Edit">
        <EditIcon
          color="action"
          style={{ cursor: 'pointer' }}
          onClick={() => history.push(`${location.pathname}/edit/${attendee.id}`)}
        />
      </Tooltip>
    ])

  const options = getTableOptions()
  return (
    <>
      <Box className={classes.headBar}>
        <Typography variant="h5" className={classes.headerTitle}>
          Attendees
        </Typography>
        {usesConferenceFilter && (
          <Box className={classes.actions}>
            <Box className={classes.filterDropdown}>
              <Typography className={classes.filterLabel}>Conference: </Typography>
              {confProgress ? (
                <CircularProgress size={25} />
              ) : (
                <FormControl fullWidth variant="outlined" size="small" className={classes.select}>
                  <Select
                    value={confFilter}
                    onChange={(event) =>
                      dispatch(attendeeSlice.actions.setConferenceFilter(event.target.value))
                    }>
                    {orderedConferences.map((conf) => (
                      <MenuItem value={conf?.id} key={conf?.id}>
                        {conf?.confName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {isSuperAdmin && (
                <Box className={classes.downloadCSV}>
                  <Button
                    size="large"
                    className={classes.button}
                    startIcon={<Download />}
                    variant="contained"
                    color="primary"
                    onClick={handleDownloadCSV}>
                    Download Attendees
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>

      <Wrapper inProgress={inProgress}>
        {attendees && attendees.length ? (
          <Paper variant="outlined" style={{ marginTop: 20 }}>
            <BasicTable
              data={formatTableData(attendees)}
              columns={tableHead}
              options={{
                ...options,
                onChangePage: (page) => dispatch(attendeeSlice.actions.setCurrentPage(page)),
                onChangeRowsPerPage: (size) => dispatch(attendeeSlice.actions.setPageSize(size)),
                page: currentPage,
                rowsPerPage: pageSize
              }}
            />
          </Paper>
        ) : (
          !inProgress && <ResourceNotFound message="No attendee available for this conference" />
        )}
      </Wrapper>
    </>
  )
}

export default AttendeesListing

const useStyles = makeStyles({
  headBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0px 4px 12px 4px'
  },
  headerTitle: {
    fontWeight: 600,
    color: '#263238'
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  button: {
    minWidth: '250px'
  },
  select: {
    minWidth: '200px'
  },
  filterDropdown: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  filterLabel: {
    width: '100%',
    fontSize: '1rem',
    textAlign: 'right',
    marginRight: '8px',
    fontWeight: 'bold'
  },
  downloadCSV: { width: '100%', paddingLeft: '1.25rem' },
  selectBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px'
  },
  iconBtn: {
    padding: '4px'
  },
  icon: {
    fontSize: '18px'
  },
  actionBtns: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  flexBox: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center'
  }
})
